/**
 * Kontaktliste und -detail (Spec 7.2 „Kundenkontext-Panel", Phase 1).
 *
 * Das Detail zeigt Identitaeten, Notizen und alle Tickets des Kontakts ueber
 * alle Quellen — genau das, was ein Agent im Gespraech braucht.
 */
import type { Contact, ContactDetail, TicketListItem } from '@tickethub/shared';
import { roleHasPermission } from '@tickethub/shared';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toastError } from '@/lib/errors';
import { useSession } from '@/lib/session';
import { contactsApi, ticketsApi } from '@/lib/tickets';

export function ContactsPage() {
  const { t } = useTranslation();
  const { activeMembership } = useSession();
  const tenantId = activeMembership?.tenant_id ?? null;

  const [contacts, setContacts] = useState<Contact[] | null>(null);
  const [search, setSearch] = useState('');
  const [submitted, setSubmitted] = useState('');

  useEffect(() => {
    if (!tenantId) return;
    void (async () => {
      try {
        setContacts((await contactsApi.list(tenantId, submitted || undefined)).items);
      } catch (error) {
        setContacts([]);
        toastError(error, t);
      }
    })();
  }, [tenantId, submitted, t]);

  if (!activeMembership) {
    return (
      <Alert>
        <AlertDescription>{t('tenant.empty')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">{t('contact.list_title')}</h1>

      <form
        className="flex items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(search.trim());
        }}
      >
        <label className="flex-1 max-w-md">
          <span className="mb-1 block text-xs font-medium">{t('contact.search')}</span>
          <Input type="search" value={search} onChange={(event) => setSearch(event.target.value)} />
        </label>
        <Button type="submit">{t('contact.search_submit')}</Button>
      </form>

      {contacts === null ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : contacts.length === 0 ? (
        <Alert>
          <AlertDescription>{t('contact.empty')}</AlertDescription>
        </Alert>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">{t('contact.name')}</th>
                <th className="px-4 py-2 font-medium">{t('contact.email')}</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact) => (
                <tr key={contact.id} className="border-t hover:bg-muted/30">
                  <td className="px-4 py-2">
                    <Link className="font-medium hover:underline" to={`/contacts/${contact.id}`}>
                      {contact.display_name}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {contact.primary_email ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function ContactDetailPage() {
  const { t } = useTranslation();
  const { contactId = '' } = useParams();
  const { activeMembership } = useSession();
  const tenantId = activeMembership?.tenant_id ?? null;
  const canWrite = activeMembership
    ? roleHasPermission(activeMembership.role, 'ticket.write')
    : true;

  const [contact, setContact] = useState<ContactDetail | null>(null);
  const [tickets, setTickets] = useState<TicketListItem[]>([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId || !contactId) return;
    try {
      const detail = await contactsApi.get(tenantId, contactId);
      setContact(detail);
      setNotes(detail.notes ?? '');
      setTickets(
        (await ticketsApi.list(tenantId, { contact_id: contactId, per_page: 50 })).items,
      );
    } catch (error) {
      toastError(error, t);
    }
  }, [tenantId, contactId, t]);

  useEffect(() => {
    // `load`/`reload` setzen State erst *nach* dem Netzwerk-Await — es entsteht
    // keine kaskadierende Render-Schleife. Die Regel kann das nicht unterscheiden.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Daten beim Mount laden
    void load();
  }, [load]);

  async function saveNotes() {
    if (!tenantId) return;
    setSaving(true);
    try {
      await contactsApi.update(tenantId, contactId, { notes });
    } catch (error) {
      toastError(error, t);
    } finally {
      setSaving(false);
    }
  }

  if (!contact) {
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{contact.display_name}</h1>
        <p className="text-sm text-muted-foreground">{contact.primary_email ?? '—'}</p>
      </div>

      <section className="space-y-2">
        <h2 className="text-lg font-medium">{t('contact.identities')}</h2>
        {contact.identities.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('contact.no_identities')}</p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {contact.identities.map((identity) => (
              <li key={identity.id}>
                <Badge variant="outline">
                  {t(`contact.identity_${identity.type}`)}: {identity.value}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-2">
        <Label htmlFor="contact-notes">{t('contact.notes')}</Label>
        <textarea
          id="contact-notes"
          className="min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          value={notes}
          disabled={!canWrite}
          onChange={(event) => setNotes(event.target.value)}
        />
        {canWrite ? (
          <Button size="sm" disabled={saving} onClick={() => void saveNotes()}>
            {saving ? t('common.loading') : t('common.save')}
          </Button>
        ) : null}
      </section>

      <section className="space-y-2">
        <h2 className="text-lg font-medium">
          {t('contact.tickets', { count: contact.ticket_count })}
        </h2>
        {tickets.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('ticket.empty')}</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {tickets.map((ticket) => (
              <li key={ticket.id}>
                <Link className="hover:underline" to={`/tickets/${ticket.id}`}>
                  <span className="font-mono text-xs text-muted-foreground">{ticket.number}</span>{' '}
                  {ticket.subject}
                </Link>{' '}
                <Badge variant="outline">{t(`ticket.status_${ticket.status}`)}</Badge>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
